import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { StatusPill } from '../components/StatusPill.js'

export const Route = createFileRoute('/_app/super/academies/')({
  component: SuperAcademiesPage,
})

interface AcademyRow {
  id: string
  name: string
  slug: string
  email: string
  city: string | null
  state: string | null
  status: 'trial' | 'active' | 'suspended' | 'cancelled'
  planId: string | null
  planName: string | null
  trialEndsAt: string | null
  createdAt: string
}

interface PendingInvite {
  id: string
  email: string
  fullName: string | null
  invitedAt: string
}

interface RevenueRow {
  mrrInr: number
  pendingInr: number
  failedInvoices: number
}

interface PlanOption {
  id: string
  name: string
  status: 'published' | 'unpublished' | 'deprecated'
}

const STATUS_OPTIONS: AcademyRow['status'][] = ['trial', 'active', 'suspended', 'cancelled']

function SuperAcademiesPage() {
  const qc = useQueryClient()

  const academies = useQuery({
    queryKey: ['super', 'academies'],
    queryFn: async () => {
      const res = await api<AcademyRow[]>('/api/v1/super/academies')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // All plans (published OR not) so a super admin can assign any plan, including
  // unpublished ones, straight from this list.
  const plans = useQuery({
    queryKey: ['super', 'plans'],
    queryFn: async () => {
      const res = await api<PlanOption[]>('/api/v1/super/plans')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // Invited-but-not-yet-onboarded admins. They have no academies row yet, so
  // they don't appear in the main list — surface them here so a super admin can
  // see who's been invited and re-send if needed.
  const pendingInvites = useQuery({
    queryKey: ['super', 'pending-invites'],
    queryFn: async () => {
      const res = await api<PendingInvite[]>('/api/v1/super/academies/pending-invites')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const revenue = useQuery({
    queryKey: ['super', 'revenue'],
    queryFn: async () => {
      const res = await api<RevenueRow>('/api/v1/super/revenue')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [statusError, setStatusError] = useState<string | null>(null)

  const updateStatus = useMutation({
    mutationFn: async (vars: { id: string; status: AcademyRow['status'] }) => {
      const res = await api<AcademyRow>(`/api/v1/super/academies/${vars.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: vars.status }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    // Optimistic: flip the row in the cache immediately so the STATUS pill +
    // count cards update without waiting for the round-trip. Roll back on error.
    onMutate: async (vars) => {
      setStatusError(null)
      await qc.cancelQueries({ queryKey: ['super', 'academies'] })
      const previous = qc.getQueryData<AcademyRow[]>(['super', 'academies'])
      qc.setQueryData<AcademyRow[]>(['super', 'academies'], (rows) =>
        (rows ?? []).map((a) => (a.id === vars.id ? { ...a, status: vars.status } : a)),
      )
      return { previous }
    },
    // Trust the server's returned row — write the confirmed status into the
    // cache. We intentionally do NOT refetch here: an immediate re-read can hit
    // read-after-write lag (Hyperdrive/Neon) and return the pre-update row,
    // which would clobber the change and make the status appear to revert.
    onSuccess: (updated) => {
      qc.setQueryData<AcademyRow[]>(['super', 'academies'], (rows) =>
        (rows ?? []).map((a) =>
          a.id === updated.id ? { ...a, status: updated.status } : a,
        ),
      )
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['super', 'academies'], ctx.previous)
      setStatusError(
        err instanceof Error ? err.message : 'Could not update status. Try again.',
      )
    },
  })

  // Assign a plan to an academy directly from the list. Any plan is allowed,
  // including unpublished ones (the backend only checks the plan exists). Same
  // optimistic + trust-the-returned-row pattern as the status change.
  const updatePlan = useMutation({
    mutationFn: async (vars: { id: string; planId: string | null }) => {
      const res = await api<AcademyRow>(`/api/v1/super/academies/${vars.id}/plan`, {
        method: 'PATCH',
        body: JSON.stringify({ planId: vars.planId }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onMutate: async (vars) => {
      setStatusError(null)
      await qc.cancelQueries({ queryKey: ['super', 'academies'] })
      const previous = qc.getQueryData<AcademyRow[]>(['super', 'academies'])
      const planName = plans.data?.find((p) => p.id === vars.planId)?.name ?? null
      qc.setQueryData<AcademyRow[]>(['super', 'academies'], (rows) =>
        (rows ?? []).map((a) =>
          a.id === vars.id ? { ...a, planId: vars.planId, planName } : a,
        ),
      )
      return { previous }
    },
    onSuccess: (updated) => {
      // The PATCH returns the base academy row (no join-derived planName), so
      // merge rather than replace to keep the optimistic planName label.
      qc.setQueryData<AcademyRow[]>(['super', 'academies'], (rows) =>
        (rows ?? []).map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
      )
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['super', 'academies'], ctx.previous)
      setStatusError(
        err instanceof Error ? err.message : 'Could not assign plan. Try again.',
      )
    },
  })

  // Invite a new academy (invite-only registration): create the admin's account
  // and email them a verification link to finish setting up their academy.
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSent, setInviteSent] = useState<string | null>(null)

  const invite = useMutation({
    mutationFn: async () => {
      const res = await api<{ email: string; status: 'invited' | 'resent' }>(
        '/api/v1/super/academies/invite',
        {
          method: 'POST',
          body: JSON.stringify({ email: inviteEmail, fullName: inviteName }),
        },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      setInviteSent(
        data.status === 'resent'
          ? `Invite re-sent to ${data.email}.`
          : `Invite sent to ${data.email}.`,
      )
      setInviteEmail('')
      setInviteName('')
      setInviteError(null)
      // A brand-new invite adds a pending-invite row; refresh so it shows up.
      void qc.invalidateQueries({ queryKey: ['super', 'pending-invites'] })
    },
    onError: (err) => {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invite')
      setInviteSent(null)
    },
  })

  // Re-send a pending invite's verification email. The invite endpoint treats a
  // repeat POST for a still-pending invite as a resend (status 'resent').
  const [resendMsg, setResendMsg] = useState<string | null>(null)
  const resendInvite = useMutation({
    mutationFn: async (inv: PendingInvite) => {
      const res = await api<{ email: string; status: 'invited' | 'resent' }>(
        '/api/v1/super/academies/invite',
        {
          method: 'POST',
          body: JSON.stringify({ email: inv.email, fullName: inv.fullName ?? inv.email }),
        },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      setResendMsg(`Invite re-sent to ${data.email}.`)
    },
    onError: (err) => {
      setResendMsg(err instanceof Error ? err.message : 'Could not re-send invite.')
    },
  })

  const counts = academies.data
    ? {
        total: academies.data.length,
        active: academies.data.filter((a) => a.status === 'active').length,
        trial: academies.data.filter((a) => a.status === 'trial').length,
      }
    : null

  const columns: Column<AcademyRow>[] = [
    {
      key: 'name',
      header: 'Academy',
      render: (a) => (
        <div>
          <Link
            to="/super/academies/$id"
            params={{ id: a.id }}
            className="font-medium text-fg hover:text-[var(--color-primary)]"
          >
            {a.name}
          </Link>
          <div className="text-xs text-fg-subtle">
            {a.slug} · {[a.city, a.state].filter(Boolean).join(', ') || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (a) => (
        <select
          value={a.planId ?? ''}
          onChange={(e) =>
            updatePlan.mutate({
              id: a.id,
              planId: e.target.value === '' ? null : e.target.value,
            })
          }
          className="text-xs rounded border border-border bg-surface px-2 py-1 hover:border-border-strong focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] cursor-pointer"
        >
          <option value="">No plan</option>
          {(plans.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.status !== 'published' ? ` (${p.status})` : ''}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => <StatusPill tone={a.status}>{a.status}</StatusPill>,
    },
    {
      key: 'created',
      header: 'Joined',
      align: 'right',
      render: (a) => (
        <span className="text-sm text-fg-muted">
          {new Date(a.createdAt).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'trial',
      header: 'Trial ends',
      align: 'right',
      render: (a) => (
        <span className="text-sm text-fg-muted">
          {a.trialEndsAt ? new Date(a.trialEndsAt).toLocaleDateString('en-IN') : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Set status',
      render: (a) => (
        <select
          value={a.status}
          onChange={(e) =>
            updateStatus.mutate({
              id: a.id,
              status: e.target.value as AcademyRow['status'],
            })
          }
          className="text-xs rounded border border-border bg-surface px-2 py-1 hover:border-border-strong focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] capitalize cursor-pointer"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s} className="capitalize">
              {s}
            </option>
          ))}
        </select>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title="Academies"
        description="All tenants on the platform."
        actions={
          <Button onClick={() => setShowInvite((v) => !v)}>
            {showInvite ? 'Close' : 'Invite academy'}
          </Button>
        }
      />

      {showInvite ? (
        <Card>
          <CardBody>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault()
                invite.mutate()
              }}
            >
              <p className="text-sm text-fg-muted">
                Invite a new academy owner. They&apos;ll get an email to verify
                their address and set up their academy. Submitting the same email
                again re-sends the invite if they haven&apos;t verified yet.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Admin name" required>
                  <Input
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    required
                    placeholder="Priya Sharma"
                  />
                </Field>
                <Field label="Admin email" required>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    placeholder="owner@academy.com"
                  />
                </Field>
              </div>
              {inviteError ? (
                <p className="rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
                  {inviteError}
                </p>
              ) : null}
              {inviteSent ? (
                <p className="rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
                  {inviteSent}
                </p>
              ) : null}
              <Button type="submit" loading={invite.isPending}>
                Send invite
              </Button>
            </form>
          </CardBody>
        </Card>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Tenants</p>
            <p className="mt-1 text-2xl font-semibold text-fg">
              {counts ? counts.total : '—'}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Active</p>
            <p className="mt-1 text-2xl font-semibold text-success">
              {counts ? counts.active : '—'}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">On trial</p>
            <p className="mt-1 text-2xl font-semibold text-warning">
              {counts ? counts.trial : '—'}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs text-fg-subtle">Failed invoices</p>
            <p
              className={`mt-1 text-2xl font-semibold ${
                revenue.data?.failedInvoices ? 'text-danger' : 'text-fg'
              }`}
            >
              {revenue.data ? revenue.data.failedInvoices : '—'}
            </p>
          </CardBody>
        </Card>
      </div>

      {statusError ? (
        <p className="rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
          {statusError}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={academies.data ?? []}
        rowKey={(a) => a.id}
        loading={academies.isLoading}
      />

      {(pendingInvites.data?.length ?? 0) > 0 ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-fg">
              Pending invites ({pendingInvites.data!.length})
            </h2>
            <p className="text-xs text-fg-subtle">
              Invited admins who haven&apos;t verified their email and set up their
              academy yet. They&apos;ll move into the list above once they finish
              onboarding.
            </p>
          </div>
          {resendMsg ? (
            <p className="rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
              {resendMsg}
            </p>
          ) : null}
          <Card>
            <CardBody className="divide-y divide-border">
              {pendingInvites.data!.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-fg">
                      {inv.fullName || inv.email}
                    </p>
                    <p className="truncate text-xs text-fg-subtle">
                      {inv.email} · invited{' '}
                      {new Date(inv.invitedAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusPill tone="trial">Pending</StatusPill>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setResendMsg(null)
                        resendInvite.mutate(inv)
                      }}
                      loading={resendInvite.isPending && resendInvite.variables?.id === inv.id}
                    >
                      Re-send
                    </Button>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </section>
      ) : null}
    </div>
  )
}
